/**
 * The decision this whole feature turns on: block them, offer it, or say
 * nothing. See USER_FORM_PROMPTS_PLAN.md in the API repo.
 *
 * Pure and request-free, because the same answer has to come out here, in the
 * app and on the server. The client re-decides locally — a prompt can expire, or
 * a snooze run out, while a session is open — and a client that disagreed with
 * the server would either nag someone who has finished or, worse, let someone
 * walk past a required prompt.
 *
 * Mirrors the API's `UserFormPromptEligibilityExtensions` and the Flutter app's
 * `user_form_prompt_eligibility.dart` case for case — change all three together.
 *
 * THE SET CARRIES LIVENESS. Whether this run happens at all — draft, platform,
 * publish time, expiry, the build minimum — is decided from the SET (a "pack" in
 * the API), because those are properties of the run rather than of one form
 * inside it. What the FORM still decides is whether it has been answered and
 * whether it blocks.
 *
 * AUDIENCE IS NOT DECIDED HERE. Whether a set is aimed at this user needs the
 * database, so the server settles it before sending anything; everything below
 * assumes the set already applies to them.
 *
 * House style: imported explicitly, never auto-imported.
 */

export const USER_FORM_PROMPT_MODE = Object.freeze({
    optional: 'optional',
    required: 'required',
});

export const USER_FORM_PROMPT_STATUS = Object.freeze({
    snoozed: 'snoozed',
    dismissed: 'dismissed',
    completed: 'completed',
});

export const USER_FORM_PROMPT_SURFACE = Object.freeze({
    web: 'web',
    app: 'app',
});

export const USER_FORM_PROMPT_OUTCOME = Object.freeze({
    /** Say nothing. */
    ignore: 'ignore',
    /** Offer it; it may be dismissed or snoozed. */
    prompt: 'prompt',
    /** Hold the site until it is answered. */
    block: 'block',
});

/**
 * The routes a required set never holds.
 *
 * The legal pages the form links to, the profile page (delete account, export),
 * contact, the email one-click pages that must never be hijacked, the setup
 * screen that runs before this one, and the super admin console — see
 * `promptWallDestination` for why that last one is here.
 */
export const USER_FORM_PROMPT_EXITS = Object.freeze([
    '/prompt', '/details-required', '/legal', '/contact', '/your-profile',
    '/meets/offer/', '/pay/', '/embed', '/admin/super',
    // Stopping an email must always work, whatever is waiting (29 Sep 2026).
    '/email-preferences',
]);

export function useUserFormPrompts() {
    /**
     * Targeting no platform is an admin mistake, not an instruction to block the
     * world, so an empty target reaches nobody.
     */
    const targetsSurface = (platforms, surface) => {
        const p = platforms ?? {};
        return surface === USER_FORM_PROMPT_SURFACE.app ? p.app === true : p.web === true;
    };

    /**
     * A dotted version, mirroring .NET's `Version.TryParse` — which is what the
     * API uses, and what `AppVersionMiddleware` already parses the `appVersion`
     * header with. Two to four non-negative integer parts; anything else is
     * unreadable, and an unreadable version fails closed rather than being
     * guessed at.
     */
    const parseVersion = (raw) => {
        if (typeof raw !== 'string') return null;
        const parts = raw.trim().split('.');
        if (parts.length < 2 || parts.length > 4) return null;

        const parsed = [];
        for (const part of parts) {
            if (!/^\d+$/.test(part)) return null;
            parsed.push(Number(part));
        }
        return parsed;
    };

    /**
     * Compares two parsed versions the way .NET does, which is the point: an
     * unspecified component counts as -1, NOT as zero. So "9.1" is LESS than
     * "9.1.0" on every platform. Padding with zeroes instead would make a client
     * accept a build the server rejects, which is exactly the drift this
     * mirroring exists to prevent.
     */
    const compareVersions = (a, b) => {
        for (let i = 0; i < 4; i++) {
            const left = i < a.length ? a[i] : -1;
            const right = i < b.length ? b[i] : -1;
            if (left !== right) return left < right ? -1 : 1;
        }
        return 0;
    };

    /**
     * Whether this client has a renderer for the form.
     *
     * The website always has one — it ships from one place and is never behind.
     * An app build below the minimum is sent NOTHING rather than something it
     * would fail to draw; showing an old build a required prompt it cannot
     * render would lock it out with no way through.
     *
     * Fails CLOSED for the app on anything it cannot read.
     */
    const buildCanDrawIt = (minAppVersion, viewer) => {
        if (!minAppVersion || !String(minAppVersion).trim()) return true;
        if (viewer.surface === USER_FORM_PROMPT_SURFACE.web) return true;

        const minimum = parseVersion(minAppVersion);
        if (minimum === null) return false;

        const current = parseVersion(viewer.appVersion);
        if (current === null) return false;

        return compareVersions(current, minimum) >= 0;
    };

    const asDate = (value) => (value instanceof Date ? value : new Date(value));

    /**
     * Block them, offer it, or say nothing.
     *
     * Order matters, and the awkward cases are the reason:
     *
     *   1. Reasons the prompt is not live at all come first, so an expired or
     *      mistargeted prompt can never block anyone. A required prompt that
     *      outlived its expiry would be a wall nobody can take down without a
     *      deploy.
     *   2. Then whether this build can even draw it.
     *   3. Then a completed answer at the current version, which releases a
     *      required prompt.
     *   4. Then `required`, ABOVE dismissal and snoozing — both are only ever
     *      legal on an optional prompt, so a required one carrying either means
     *      its mode was changed afterwards, and honouring the stale escape would
     *      walk someone straight past it.
     */
    const decideUserFormPrompt = (pack, prompt, response, viewer) => {
        const now = asDate(viewer.now);
        const set = pack ?? {};

        // Authored but not live. First, because it is the most absolute reason
        // of the lot: a draft is not a set anyone is meant to meet yet, so
        // nothing about platforms, timing or mode can make it reachable. The
        // server never sends one, so this only matters to a client re-deciding
        // locally — which is exactly what this mirror is for.
        if (set.isDraft) {
            return USER_FORM_PROMPT_OUTCOME.ignore;
        }

        if (!targetsSurface(set.platforms, viewer.surface)) {
            return USER_FORM_PROMPT_OUTCOME.ignore;
        }

        if (asDate(set.publishAt) > now) {
            return USER_FORM_PROMPT_OUTCOME.ignore;
        }

        // Expiring "now" has expired: an end time is the moment it stops, not
        // the last moment it counts.
        if (set.expiresAt != null && asDate(set.expiresAt) <= now) {
            return USER_FORM_PROMPT_OUTCOME.ignore;
        }

        // The whole SET's minimum, not this form's: the run is introduced as one
        // thing, so a build that cannot draw the third form must not be shown
        // the card for a run containing it.
        if (!buildCanDrawIt(set.minAppVersion, viewer)) {
            return USER_FORM_PROMPT_OUTCOME.ignore;
        }

        const promptVersion = prompt.schemaVersion ?? 1;

        // Answered the questions as they stand. Bumping schemaVersion is what
        // asks again, which is how terms re-acceptance works.
        if (response?.status === USER_FORM_PROMPT_STATUS.completed &&
            (response.schemaVersion ?? 1) >= promptVersion) {
            return USER_FORM_PROMPT_OUTCOME.ignore;
        }

        if (prompt.mode === USER_FORM_PROMPT_MODE.required) {
            return USER_FORM_PROMPT_OUTCOME.block;
        }

        // Final, and deliberately not version sensitive: fixing a typo must not
        // re-nag everyone who already said no, and a genuine rewrite is a new
        // set rather than a reason to go back to people who declined this one.
        if (response?.status === USER_FORM_PROMPT_STATUS.dismissed) {
            return USER_FORM_PROMPT_OUTCOME.ignore;
        }

        if (response?.status === USER_FORM_PROMPT_STATUS.snoozed &&
            response.snoozedUntil != null &&
            asDate(response.snoozedUntil) > now) {
            return USER_FORM_PROMPT_OUTCOME.ignore;
        }

        return USER_FORM_PROMPT_OUTCOME.prompt;
    };

    /**
     * Where a navigation has to go while a required set is waiting, or null to
     * let it through.
     *
     * Pure, and separate from the middleware, because the EXITS are the whole of
     * the escape hatch on this platform: the wall is the route, so a route the
     * list forgets is a route nobody can reach until the set comes down. That is
     * worth a test rather than a constant nobody can exercise.
     *
     * `/admin/super` is an exit and has to be. A required set applies to the
     * super admin like anyone else — correctly, they are a member too — but the
     * console at /admin/super/prompts is the one place a set can be withdrawn,
     * and a required set that CANNOT be answered (the region question needs
     * Google and has no manual fallback) would otherwise leave the database as
     * the only way to take it down, on the day it went out to fourteen thousand
     * people. The route is super admin gated by its own middleware, so letting
     * it past this one leaks nothing.
     *
     * The club admin pages are deliberately NOT exits. They are the product, and
     * a club admin is an ordinary member as far as the terms are concerned.
     */
    const promptWallDestination = (path, pending) => {
        if (!pending?.id) return null;
        // The run's card is required whenever anything left in it is a wall
        // for this member, so it holds the page even if the form served first
        // is one they could have skipped on its own (UAT, 27 Sep 2026).
        const wall = pending.mode === USER_FORM_PROMPT_MODE.required
            || pending.card?.mode === USER_FORM_PROMPT_MODE.required;
        if (!wall) return null;
        if (isPromptWallExit(path)) return null;

        return '/prompt?redirectUrl=' + encodeURIComponent(path);
    };

    /**
     * Whether this route is one the wall never holds.
     *
     * Shared by the destination above and the mount check below, so the escape
     * hatch cannot become two lists that disagree. A route only one of them
     * knows about is either a wall with a hole in it or a page that spends a
     * request it did not need.
     */
    const isPromptWallExit = (path) => {
        const justThePath = String(path ?? '').split('?')[0];
        return USER_FORM_PROMPT_EXITS.some(p => justThePath === p || justThePath.startsWith(p));
    };

    /**
     * Whether the app's own mount should spend a request re-reading the account
     * before it decides about the wall.
     *
     * The wall is the route, and the route middleware reads whatever user the
     * session happens to be holding. That user is refreshed at every launch
     * (`init` awaits `updateLocalUser`), but the refresh SWALLOWS its errors on
     * purpose: an unreachable API must not log anybody out. So a launch whose
     * refresh did not land carries on with the copy the cookie started it with,
     * and `pendingFormPrompt` is a field only the refresh can add. The wall then
     * quietly does not engage, which is the one way a required set can be walked
     * past without anybody doing anything wrong.
     *
     * `refreshed` is what keeps this cheap: in the ordinary case the launch
     * refresh landed, nothing is spent, and the mount check simply applies the
     * wall to the user it already has. Exits are skipped because the answer
     * there is "let them through" whatever the account says.
     */
    const promptWallShouldRefresh = ({authenticated, refreshed, path} = {}) => {
        if (!authenticated) return false;
        if (refreshed) return false;
        return !isPromptWallExit(path);
    };

    return {decideUserFormPrompt, promptWallDestination, isPromptWallExit, promptWallShouldRefresh};
}
